import { Fragment, useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { useAnalyticsWrapper } from "../../../../../store/store";
import { Child, Container, Header, LayerContent } from "../style";
import { ChartContainer, NoData } from "../../style";
import { handleFormatter } from "../formUtils";
import SectionModal from "./SectionModal";
import { Grid, GridColumn, GridColumnProps } from "@progress/kendo-react-grid";
import {
  CenteredWholeNumCell,
  HeaderCenteredCell,
} from "../../../../../components/DynamicGrid/customCells";
import { TotalRow } from "../../../../../components/DynamicGrid/customRows";
import {
  handleCSVDownload,
  handlePDFDownload,
} from "../../../../../utils/helpers/pdfCsvUtils";
import { woundAnalysisDetailCols } from "../../dummyData/colums";

// Section 3 — Wound Origin (House Acquired vs On Admission), stacked New vs Reoccurrence.
// Reads woundAnalysisIncidents.woundOrigin (originBreakdown + drillDownByOrigin).
const stackedBarOptions = (onBarClick: (chart: any) => void) => ({
  responsive: true,
  plugins: {
    legend: { display: true, position: "top" as const },
    datalabels: {
      display: true,
      color: "#fff",
      formatter: (value: any) => handleFormatter(value),
      font: { size: 14 },
    },
  },
  scales: {
    // Bold the origin (House Acquired / On Admission) axis labels so it's clear what's being shown.
    x: { stacked: true, ticks: { font: { weight: "bold" as const, size: 13 } } },
    y: { stacked: true, ticks: { display: false } },
  },
  onClick: (_e: any, chart: any) => onBarClick(chart),
  onHover: (e: any, el: any) => {
    e.native.target.style.cursor = el[0] ? "pointer" : "default";
  },
});

const WoundOrigin = ({ setClickedIncident }: any) => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [grid, setGrid] = useState<any>([]);
  const [chartData, setChartData] = useState<any>(null);
  const [origin, setOrigin] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [originDetails, setOriginDetails] = useState<any>([]);

  const { startDate, endDate, woundFacility, woundAnalysisIncidents } =
    useAnalyticsWrapper((state) => ({
      startDate: state.startDate,
      endDate: state.endDate,
      woundFacility: state.woundFacility,
      woundAnalysisIncidents: state.woundAnalysisIncidents,
    }));

  useEffect(() => {
    const breakdown = woundAnalysisIncidents?.woundOrigin?.originBreakdown;
    if (breakdown?.length) {
      const rows = breakdown.map((row: any) => ({
        origin: row.origin,
        new: row.new,
        reoccurrence: row.reoccurrence,
        total: row.total,
        percentage: row.percentage,
      }));
      const totals = rows.reduce(
        (acc: any, r: any) => ({
          new: acc.new + (r.new ?? 0),
          reoccurrence: acc.reoccurrence + (r.reoccurrence ?? 0),
          total: acc.total + (r.total ?? 0),
        }),
        { new: 0, reoccurrence: 0, total: 0 }
      );
      rows.push({
        origin: "Total",
        new: totals.new,
        reoccurrence: totals.reoccurrence,
        total: totals.total,
        percentage: 100,
      });
      setTableData(rows);
      setChartData({
        labels: breakdown.map((r: any) => r.origin),
        datasets: [
          {
            label: "New",
            data: breakdown.map((r: any) => r.new ?? 0),
            backgroundColor: "#5B8EC4",
          },
          {
            label: "Reoccurrence",
            data: breakdown.map((r: any) => r.reoccurrence ?? 0),
            backgroundColor: "#4ABCB0",
          },
        ],
      });
    } else {
      setTableData([]);
      setChartData(null);
    }
  }, [woundAnalysisIncidents]);

  const title = "Wound Origin — On Admission vs House Acquired";

  // Segment-level drill-down: distinguish New vs Reoccurrence by datasetIndex, origin by bar index.
  const handleBarClicked = (chart: any) => {
    if (!chart.length) return;
    const el = chart[0];
    const clickedOrigin = chartData?.labels?.[el.index];
    const status = el.datasetIndex === 1 ? "Reoccurrence" : "New";
    const byOrigin =
      woundAnalysisIncidents?.woundOrigin?.drillDownByOrigin?.[clickedOrigin];
    // Robust to both shapes: nested { New: [], Reoccurrence: [] } (current) or a legacy flat array.
    let details: any[] = [];
    if (Array.isArray(byOrigin)) {
      details = byOrigin; // legacy/stale flat payload — show the whole origin
      setOrigin(clickedOrigin);
    } else if (byOrigin) {
      details = byOrigin[status] || [];
      setOrigin(`${clickedOrigin} — ${status}`);
    } else {
      setOrigin(`${clickedOrigin} — ${status}`);
    }
    setOriginDetails(details);
    setShowModal(true);
  };

  useEffect(() => {
    const cellCountProps = {
      cells: { data: CenteredWholeNumCell, headerCell: HeaderCenteredCell },
    };
    const cellPercentageProps = {
      cells: {
        data: CenteredWholeNumCell,
        append: "%",
        headerCell: HeaderCenteredCell,
      },
    };
    const newGrid = (
      <Fragment>
        <Grid data={tableData || []} scrollable="none" rows={{ data: TotalRow }}>
          <GridColumn field="origin" title="Origin" />
          <GridColumn field="new" title="New" {...(cellCountProps as GridColumnProps)} />
          <GridColumn field="reoccurrence" title="Reoccurrence" {...(cellCountProps as GridColumnProps)} />
          <GridColumn field="total" title="Total" {...(cellCountProps as GridColumnProps)} />
          <GridColumn field="percentage" title="%" {...(cellPercentageProps as GridColumnProps)} />
        </Grid>
      </Fragment>
    );
    setGrid(newGrid);
  }, [tableData]);

  return (
    <LayerContent>
      <Header>{title}</Header>
      <Container>
        <Child flex="40%">{grid}</Child>
        <Child flex="60%">
          <ChartContainer>
            {chartData ? (
              <Bar options={stackedBarOptions(handleBarClicked)} data={chartData} />
            ) : (
              <NoData>No Data</NoData>
            )}
          </ChartContainer>
        </Child>
      </Container>
      {showModal ? (
        <SectionModal
          open={showModal}
          facilityName={(woundFacility?.label ?? woundFacility?.[0]?.label)}
          startDate={startDate}
          endDate={endDate}
          closeFn={() => setShowModal(false)}
          resetFn={() => {}}
          isLoading={false}
          tableData={originDetails}
          tableCols={woundAnalysisDetailCols()}
          title={`${title} - ${origin}`}
          getUserCallback={(item: any) => setClickedIncident(item)}
          handlePdfExport={() =>
            handlePDFDownload({
              data: originDetails,
              columnData: woundAnalysisDetailCols(),
              fileName: (woundFacility?.label ?? woundFacility?.[0]?.label),
              startDate,
              endDate,
              subTitle: `${title} - ${origin}`,
            })
          }
          handleCsvExport={() =>
            handleCSVDownload(
              originDetails,
              woundAnalysisDetailCols(),
              `${(woundFacility?.label ?? woundFacility?.[0]?.label)} - ${title} - ${origin}`
            )
          }
        />
      ) : null}
    </LayerContent>
  );
};

export default WoundOrigin;
